import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Users, CheckCircle2, Clock, Ban, TrendingUp, Trash2, Eye, Search, Pencil, X, Download, Mail, ArrowLeftRight, HardHat, UserCheck, BadgeCheck, PowerOff, Stethoscope, Check, Power, ClipboardList, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Metric, StatusBadge, EditContractorModal } from "./AdminDashboardPage";
import { printFormXI, printScreeningForm, printIdentityCard } from "../components/WorkmenPrintables";
import WorkmenDrawer from "../components/WorkmenDrawer";
import PayrollScreenFormDrawer from "../components/PayrollScreenFormDrawer";

// Map a Payroll Manpower contractor record into the WorkmanIn-like shape that the
// shared print helpers (Form-XI / Screening / ID Card) expect. Keys mirror
// WorkmanIn so the same templates render correctly for both data sources.
function _payrollToWorkman(c) {
  return {
    id: c.id,
    contractor_id: c.id,
    name: c.name || "",
    father_husband_name: c.father_husband_name || "",
    trade: (c.trades && c.trades[0]) || "",
    mobile: c.contact_phone || "",
    ncc_site_name: c.ncc_site_name || "",
    gender: c.gender || "",
    blood_group: c.blood_group || "",
    date_of_birth: c.date_of_birth || "",
    age_years: c.age_years || null,
    id_proof_type: c.id_proof_type || "",
    id_proof_number: c.id_proof_number || "",
    aadhaar: c.id_proof_type === "Aadhaar" ? (c.id_proof_number || "").replace(/\D/g, "") : "",
    pincode: c.pincode || "",
    village: c.village || "",
    city: c.city || "",
    state: c.state || "",
    address: c.address || "",
    nationality: c.nationality || "Indian",
    religion: c.religion || "",
    category: c.category || "",
    experience_years: c.experience_years || 0,
    marital_status: c.marital_status || "",
    children_count: c.children_count ?? null,
    mother_tongue: c.mother_tongue || "",
    other_languages: c.other_languages || "",
    emergency_contact_name: c.emergency_contact_name || "",
    emergency_contact_number: c.emergency_contact_number || "",
    vision: c.vision || "",
    height: c.height || "",
    weight: c.weight || "",
    hearing: c.hearing || "",
    physical_deformities: c.physical_deformities || "",
    medical_history: c.medical_history || "",
    phys_exam_blood_pressure: c.phys_exam_blood_pressure || "",
    phys_exam_pulse: c.phys_exam_pulse || "",
    phys_exam_refractive_error: c.phys_exam_refractive_error || "",
    phys_exam_colour_vision: c.phys_exam_colour_vision || "",
    phys_exam_disability: c.phys_exam_disability || "",
    phys_exam_arm_function: c.phys_exam_arm_function || "",
    phys_exam_leg_function: c.phys_exam_leg_function || "",
    hist_varicose: c.hist_varicose || "",
    hist_seizure: c.hist_seizure || "",
    hist_vertigo: c.hist_vertigo || "",
    hist_acrophobia: c.hist_acrophobia || "",
    hist_diabetes: c.hist_diabetes || "",
    hist_stroke: c.hist_stroke || "",
    hist_heart_diseases: c.hist_heart_diseases || "",
    hist_major_illness_surgery: c.hist_major_illness_surgery || "",
    hist_symptoms_visible: c.hist_symptoms_visible || "",
    hist_others: c.hist_others || "",
    photo_base64: c.photo_base64 || (c.photo?.url || ""),
    status: c.pay_roster_status === "approved" ? "approved" : "pending",
    id_card_no: c.id_card_no || "",
    id_card_issued_at: c.id_card_issued_at || "",
    is_active: c.is_active !== false,
  };
}

export default function StatusPage() {
  const { user, loading, can } = useAuth();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("pending");
  const [entity, setEntity] = useState("contractor"); // contractor | payroll | workmen | payroll-roster
  const [contractors, setContractors] = useState([]);
  const [workmen, setWorkmen] = useState([]);
  const [payRoster, setPayRoster] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [searchQ, setSearchQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [editingPayRoster, setEditingPayRoster] = useState(null);
  const [approving, setApproving] = useState(null);
  // Workmen edit drawer state
  const [editingWorkman, setEditingWorkman] = useState(null); // { contractor, workmanId }
  const [workmenDrawerOpen, setWorkmenDrawerOpen] = useState(false);

  async function loadStats() {
    try {
      const r = await api.get("/stats");
      setStats(r.data);
    } catch (err) {
      console.error("Failed to load stats:", err);
    }
  }

  async function loadList() {
    setLoadingList(true);
    try {
      if (entity === "workmen") {
        // Load ALL workmen (no status filter) so KPI counts are accurate.
        // Tab + search are applied client-side below.
        const r = await api.get("/workmen/all");
        setWorkmen(r.data || []);
      } else if (entity === "payroll-roster") {
        // Payroll Roster (Medical / Induction) — pull all payroll records; filter
        // by pay_roster_status client-side based on the active tab.
        const r = await api.get("/pay-roster");
        setPayRoster(r.data || []);
      } else {
        // Contractors — always pull full set; KPIs and table filters are derived client-side.
        const r = await api.get("/contractors", { params: { include_pending: true } });
        setContractors(r.data || []);
      }
    } finally {
      setLoadingList(false);
    }
  }

  useEffect(() => {
    if (!user) return;
    const id = setTimeout(() => { loadStats(); loadList(); }, 0);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, entity]);

  useEffect(() => {
    if (!user) return;
    const t = setTimeout(() => { loadList(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQ]);

  // ── Client-side filters: entity / tab / search are all applied here. ──
  // (Defined BEFORE early returns so hooks order stays stable.)
  const entityFilteredContractors = useMemo(() => {
    if (entity === "workmen" || entity === "payroll-roster") return [];
    return contractors.filter((c) => entity === "payroll" ? !!c.is_payroll : !c.is_payroll);
  }, [contractors, entity]);

  // Pay Roster (Medical / Induction) — derive visible list from pay_roster_status.
  const visiblePayRoster = useMemo(() => {
    if (entity !== "payroll-roster") return [];
    let arr = payRoster;
    if (tab !== "all") {
      const wanted = tab === "pending" ? "pending" : tab === "approved" ? "approved" : "rejected";
      arr = arr.filter((c) => (c.pay_roster_status || "not_submitted") === wanted);
    }
    const s = searchQ.trim().toLowerCase();
    if (s) arr = arr.filter((c) => [c.name, c.contact_phone, c.ncc_site_name, c.id_proof_number, ...(c.trades || [])]
      .filter(Boolean).join(" ").toLowerCase().includes(s));
    return arr;
  }, [payRoster, entity, tab, searchQ]);

  const visibleContractors = useMemo(() => {
    let arr = entityFilteredContractors;
    if (tab !== "all") arr = arr.filter((c) => (c.status || "pending") === tab);
    const s = searchQ.trim().toLowerCase();
    if (s) arr = arr.filter((c) => [c.name, c.contact_email, c.contact_phone, c.location, c.id_proof_number, ...(c.trades || [])]
      .filter(Boolean).join(" ").toLowerCase().includes(s));
    return arr;
  }, [entityFilteredContractors, tab, searchQ]);

  const visibleWorkmen = useMemo(() => {
    if (entity !== "workmen") return [];
    let arr = workmen;
    if (tab !== "all") arr = arr.filter((w) => (w.status || "pending") === tab);
    const s = searchQ.trim().toLowerCase();
    if (s) arr = arr.filter((w) => [w.name, w.contractor_name, w.mobile, w.trade, w.ncc_site_name, w.id_proof_number, w.aadhaar]
      .filter(Boolean).join(" ").toLowerCase().includes(s));
    return arr;
  }, [workmen, entity, tab, searchQ]);

  const derivedStats = useMemo(() => {
    if (entity === "workmen") {
      const total = workmen.length;
      const approved = workmen.filter((w) => w.status === "approved").length;
      const pending = workmen.filter((w) => (w.status || "pending") === "pending").length;
      const rejected = workmen.filter((w) => w.status === "rejected").length;
      const active = workmen.filter((w) => w.is_active !== false).length;
      return { total, approved, pending, rejected, totalCrew: active, totalCrewLabel: "Active" };
    }
    if (entity === "payroll-roster") {
      const total = payRoster.length;
      const approved = payRoster.filter((c) => c.pay_roster_status === "approved").length;
      const pending = payRoster.filter((c) => c.pay_roster_status === "pending").length;
      const rejected = payRoster.filter((c) => c.pay_roster_status === "rejected").length;
      const notSubmitted = payRoster.filter((c) => !c.pay_roster_status || c.pay_roster_status === "not_submitted").length;
      return { total, approved, pending, rejected, totalCrew: notSubmitted, totalCrewLabel: "Not Submitted" };
    }
    const arr = entityFilteredContractors;
    const total = arr.length;
    const approved = arr.filter((c) => c.status === "approved").length;
    const pending = arr.filter((c) => (c.status || "pending") === "pending").length;
    const rejected = arr.filter((c) => c.status === "rejected").length;
    const totalCrew = arr.reduce((sum, c) => sum + (Number(c.crew_size) || 0), 0);
    return { total, approved, pending, rejected, totalCrew, totalCrewLabel: "Total Crew" };
  }, [entity, workmen, payRoster, entityFilteredContractors]);


  if (loading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;
  // Status page is Super-Admin only — regular admins / contractors should not see headcount tiles.
  if (!user.is_super) return <Navigate to="/admin" replace />;

  async function setStatus(id, newStatus) {
    await api.patch(`/contractors/${id}/status?status=${newStatus}`);
    await Promise.all([loadStats(), loadList()]);
  }

  async function convertType(c) {
    const target = c.is_payroll ? "contractor" : "payroll";
    const targetLabel = target === "payroll" ? "Payroll Manpower" : "Contractor";
    if (!window.confirm(`Convert "${c.name}" to ${targetLabel}?`)) return;
    try {
      await api.patch(`/contractors/${c.id}/convert-type?target=${target}`);
      toast.success(`${c.name} converted to ${targetLabel}`);
      await Promise.all([loadStats(), loadList()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not convert type");
    }
  }

  async function approveWithType(c, asPayroll) {
    try {
      const currentPayroll = !!c.is_payroll;
      if (currentPayroll !== asPayroll) {
        const target = asPayroll ? "payroll" : "contractor";
        await api.patch(`/contractors/${c.id}/convert-type?target=${target}`);
      }
      await api.patch(`/contractors/${c.id}/status?status=approved`);
      toast.success(`${c.name} approved as ${asPayroll ? "Payroll Manpower" : "Contractor"}`);
      setApproving(null);
      await Promise.all([loadStats(), loadList()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not approve");
    }
  }

  async function del(id) {
    if (!window.confirm("Delete this contractor?")) return;
    await api.delete(`/contractors/${id}`);
    await Promise.all([loadStats(), loadList()]);
  }

  async function resendEmail(c) {
    if (!c.contact_email) {
      toast.error("No email on file for this contractor");
      return;
    }
    const label = c.status === "approved" ? "approval" : c.status === "rejected" ? "rejection" : "registration";
    if (!window.confirm(`Re-send ${label} email to ${c.contact_email}?`)) return;
    try {
      const r = await api.post(`/contractors/${c.id}/resend-status-email`);
      toast.success(`Email sent to ${r.data.email}`);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not send email";
      if (detail.toLowerCase().includes("domain") || detail.length > 80) {
        toast.error("Email could not be delivered", { description: detail, duration: 12000 });
      } else {
        toast.error(detail);
      }
    }
  }

  function exportCsv() {
    if (contractors.length === 0) return;
    const headers = [
      "Type", "Name", "Phone", "Email", "Village", "City", "State", "Contractor Type",
      "Current Status", "NCC Site Name", "ID Type", "ID Number",
      "Trades", "Trade Counts", "Crew Size", "Experience (yrs)",
      "Referred By", "Referred By Name", "Referred By Phone",
      "Avg Rating", "Review Count", "Status", "Created At",
    ];
    const esc = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = contractors.map((c) => [
      c.is_payroll ? "Payroll Manpower" : "Contractor",
      c.name, c.contact_phone, c.contact_email || "",
      c.village || "",
      c.city || (c.location?.split(",")[0]?.trim() || ""),
      c.state || (c.location?.includes(",") ? c.location.split(",").pop().trim() : ""),
      c.contractor_type || "", c.current_status || "", c.ncc_site_name || "",
      c.id_proof_type || "", c.id_proof_number || "",
      (c.trades || []).join("; "),
      Object.entries(c.trade_counts || {}).map(([t, n]) => `${t}:${n}`).join("; "),
      c.crew_size, c.experience_years,
      c.referred_by || "", c.referred_by_name || "", c.referred_by_phone || "",
      c.avg_rating, c.review_count, c.status,
      c.created_at ? new Date(c.created_at).toISOString().slice(0, 10) : "",
    ].map(esc).join(","));
    const csv = [headers.map(esc).join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `contractors-${tab}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  }

  const tabs = [
    { id: "pending", label: "Pending" },
    { id: "approved", label: "Approved" },
    { id: "rejected", label: "Rejected" },
    { id: "all", label: "All" },
  ];

  async function approveWorkman(w) {
    if (!window.confirm(`Approve workman "${w.name}"? They will be eligible for ID-card / Form-XI print.`)) return;
    try {
      await api.post(`/workmen/${w.id}/approve`);
      toast.success("Workman approved");
      await loadList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approve failed");
    }
  }

  async function rejectWorkman(w) {
    const reason = window.prompt(`Reject workman "${w.name}"?\nEnter rejection reason (will be emailed to the contractor):`);
    if (!reason || !reason.trim()) return;
    try {
      await api.post(`/workmen/${w.id}/reject`, { reason: reason.trim() });
      toast.success("Workman rejected");
      await loadList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reject failed");
    }
  }

  async function deleteWorkman(w) {
    if (!window.confirm(`Delete workman "${w.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/workmen/${w.id}`);
      toast.success("Workman deleted");
      await loadList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  }

  async function editWorkman(w) {
    try {
      const r = await api.get(`/contractors/${w.contractor_id}`);
      setEditingWorkman({ workmanId: w.id, contractor: r.data });
      setWorkmenDrawerOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not open editor");
    }
  }

  async function toggleWorkmanActive(w) {
    try {
      if (w.is_active === false) {
        if (!window.confirm(`Re-activate workman "${w.name}"?`)) return;
        await api.post(`/workmen/${w.id}/set-active`, { is_active: true, last_working_day: null });
        toast.success("Workman re-activated");
      } else {
        const today = new Date().toISOString().slice(0, 10);
        const lwd = window.prompt(`De-activate workman "${w.name}"?\nEnter Last Working Day (yyyy-mm-dd):`, today);
        if (!lwd || !lwd.trim()) return;
        await api.post(`/workmen/${w.id}/set-active`, { is_active: false, last_working_day: lwd.trim() });
        toast.success("Workman de-activated");
      }
      await loadList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update active state");
    }
  }

  // ============================================================
  // Pay Roster (Medical / Induction) — approve / reject from Status table
  // ============================================================
  async function approvePayRoster(c) {
    if (!window.confirm(`Approve Pay Roster for "${c.name}"? They will be eligible for Form-XI / Screening / ID Card print.`)) return;
    try {
      await api.post(`/pay-roster/${c.id}/approve`);
      toast.success("Pay Roster approved");
      await loadList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approve failed");
    }
  }

  async function rejectPayRoster(c) {
    const reason = window.prompt(`Reject Pay Roster for "${c.name}"?\nEnter rejection reason:`);
    if (!reason || !reason.trim()) return;
    try {
      await api.post(`/pay-roster/${c.id}/reject`, { reason: reason.trim() });
      toast.success("Pay Roster rejected");
      await loadList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reject failed");
    }
  }

  async function printScreeningOrIdCard(w, kind) {
    try {
      const r = await api.get(`/contractors/${w.contractor_id}`);
      if (kind === "screening") printScreeningForm(w, r.data);
      else if (kind === "idcard") printIdentityCard(w, r.data, []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load contractor for print");
    }
  }


  return (
    <div className="mx-auto max-w-screen-2xl px-4 py-10 md:px-8">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="industrial-label text-ncc-gold2">// Registration Status</div>
          <h1 className="mt-2 font-heading text-3xl font-black uppercase tracking-tight text-slate-900 md:text-5xl">Status</h1>
        </div>
      </div>

      {/* Entity toggle: Contractor / Payroll Manpower / Workmen Roster */}
      <div className="mt-6 flex flex-wrap items-center gap-3 border-l-4 border-ncc-navy bg-slate-100 px-4 py-3">
        <span className="industrial-label text-[10px] text-ncc-navy">// View</span>
        <div className="inline-flex flex-wrap overflow-hidden rounded-md border-2 border-ncc-navy">
          <button
            type="button"
            onClick={() => setEntity("contractor")}
            data-testid="status-entity-contractor"
            className={`inline-flex items-center gap-1.5 px-4 py-1.5 industrial-label text-xs transition-colors ${
              entity === "contractor" ? "bg-ncc-navy text-white" : "bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <HardHat className="h-3.5 w-3.5" /> Contractor
          </button>
          <button
            type="button"
            onClick={() => setEntity("payroll")}
            data-testid="status-entity-payroll"
            className={`inline-flex items-center gap-1.5 border-l-2 border-ncc-navy px-4 py-1.5 industrial-label text-xs transition-colors ${
              entity === "payroll" ? "bg-ncc-navy text-white" : "bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <Users className="h-3.5 w-3.5" /> Payroll Manpower
          </button>
          <button
            type="button"
            onClick={() => setEntity("workmen")}
            data-testid="status-entity-workmen"
            className={`inline-flex items-center gap-1.5 border-l-2 border-ncc-navy px-4 py-1.5 industrial-label text-xs transition-colors ${
              entity === "workmen" ? "bg-ncc-navy text-white" : "bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <UserCheck className="h-3.5 w-3.5" /> Workmen Roster
          </button>
          <button
            type="button"
            onClick={() => setEntity("payroll-roster")}
            data-testid="status-entity-payroll-roster"
            className={`inline-flex items-center gap-1.5 border-l-2 border-ncc-navy px-4 py-1.5 industrial-label text-xs transition-colors ${
              entity === "payroll-roster" ? "bg-ncc-navy text-white" : "bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <ClipboardList className="h-3.5 w-3.5" /> Payroll Roster
          </button>
        </div>
        <span className="ml-auto font-mono text-[11px] text-slate-600">
          {entity === "workmen"
            ? `${visibleWorkmen.length} workmen`
            : entity === "payroll-roster"
              ? `${visiblePayRoster.length} pay-roster record(s)`
              : `${visibleContractors.length} ${entity === "payroll" ? "payroll" : "contractor"} record(s)`}
        </span>
      </div>

      {/* KPI cards */}
      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Metric icon={<Users className="h-5 w-5" />} label="Total" value={derivedStats.total} accent="bg-white" testid="stat-total" onClick={() => setTab("all")} />
        <Metric icon={<CheckCircle2 className="h-5 w-5" />} label="Approved" value={derivedStats.approved} accent="bg-ncc-gold" testid="stat-approved" active={tab === "approved"} onClick={() => setTab("approved")} />
        <Metric icon={<Clock className="h-5 w-5" />} label="Pending" value={derivedStats.pending} accent="bg-white" testid="stat-pending" active={tab === "pending"} onClick={() => setTab("pending")} />
        <Metric icon={<Ban className="h-5 w-5" />} label="Rejected" value={derivedStats.rejected} accent="bg-white" testid="stat-rejected" active={tab === "rejected"} onClick={() => setTab("rejected")} />
        <Metric icon={<TrendingUp className="h-5 w-5" />} label={derivedStats.totalCrewLabel} value={derivedStats.totalCrew} accent="bg-ncc-navy text-white" testid="stat-crew" onClick={() => setTab("approved")} />
      </div>

      {/* Tabs + Search + Export */}
      <div className="mt-10">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b-2 border-slate-900 pb-0 print:hidden">
          <div className="flex flex-wrap items-center gap-1">
            {tabs.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
                className={`px-4 py-2 industrial-label transition-colors ${tab === t.id ? "bg-ncc-navy text-white" : "text-slate-700 hover:bg-slate-100"}`}>
                {t.label}
              </button>
            ))}
          </div>
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
            <input value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
              placeholder="Search name, location, NCC site…"
              data-testid="admin-search-input"
              className="w-full border-2 border-slate-900 bg-white py-2 pl-9 pr-9 font-mono text-sm focus:outline-none focus:bg-slate-50" />
            {searchQ && (
              <button type="button" onClick={() => setSearchQ("")}
                className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center bg-slate-100 hover:bg-ncc-gold"
                data-testid="admin-search-clear"><X className="h-3 w-3" /></button>
            )}
          </div>
          <button type="button" onClick={exportCsv} disabled={contractors.length === 0}
            data-testid="admin-export-csv-btn"
            className="inline-flex items-center gap-2 border-2 border-slate-900 bg-white px-4 py-2 industrial-label transition-all hover:-translate-y-0.5 hover:bg-ncc-gold hover:shadow-brutal-sm disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:bg-white disabled:hover:shadow-none"
            title="Export current view as CSV">
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>

        {/* Desktop table — Contractor / Payroll view */}
        {(entity === "contractor" || entity === "payroll") && (
        <div className="mt-6 hidden overflow-x-auto border-2 border-slate-900 bg-white md:block">
          <table className="w-full text-sm">
            <thead className="bg-ncc-navy text-white">
              <tr>
                {["Type", "Name", "Location", "Site / Status", "Trades", "Crew", "Exp", "Referred By", "Rating", "Status", "Actions"].map((h) => (
                  <th key={h} className="px-4 py-3 text-left industrial-label">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loadingList ? (
                <tr><td colSpan={11} className="p-6 text-center font-mono text-slate-500">Loading…</td></tr>
              ) : visibleContractors.length === 0 ? (
                <tr><td colSpan={11} className="p-6 text-center font-mono text-slate-500">No {entity === "payroll" ? "payroll manpower" : "contractor"} records found</td></tr>
              ) : visibleContractors.map((c) => (
                <tr key={c.id} className="border-b border-slate-200 align-top hover:bg-slate-50" data-testid={`admin-row-${c.id}`}>
                  <td className="px-4 py-3">
                    {c.is_payroll
                      ? <span className="inline-flex items-center border-2 border-slate-900 bg-blueprint px-2 py-0.5 text-xs font-bold tracking-wider text-white" data-testid={`admin-type-payroll-${c.id}`}>PAYROLL</span>
                      : <span className="inline-flex items-center border-2 border-slate-900 bg-ncc-gold px-2 py-0.5 text-xs font-bold tracking-wider text-slate-900" data-testid={`admin-type-contractor-${c.id}`}>CONTRACTOR</span>}
                  </td>
                  <td className="px-4 py-3 font-heading font-bold text-slate-900">{c.name}</td>
                  <td className="px-4 py-3 text-slate-700">{c.location}</td>
                  <td className="px-4 py-3">
                    {c.current_status === "NCCL" ? (
                      <div className="flex flex-col">
                        <span className="border border-slate-900 bg-ncc-gold px-1.5 py-0.5 industrial-label inline-block w-fit">NCCL</span>
                        <span className="mt-1 font-mono text-xs text-slate-700">{c.ncc_site_name || "—"}</span>
                      </div>
                    ) : (
                      <span className="border border-slate-900 bg-white px-1.5 py-0.5 industrial-label">NEW</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(c.trades || []).slice(0, 2).map((t) => {
                        const n = c.trade_counts?.[t];
                        return (
                          <span key={t} className="border border-slate-900 px-1.5 py-0.5 industrial-label">
                            {t}{n ? ` · ${n}` : ""}
                          </span>
                        );
                      })}
                      {(c.trades || []).length > 2 && <span className="industrial-label text-slate-500">+{c.trades.length - 2}</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono">{c.crew_size}</td>
                  <td className="px-4 py-3 font-mono">{c.experience_years}y</td>
                  <td className="px-4 py-3" data-testid={`admin-referred-by-${c.id}`}>
                    {c.referred_by ? (
                      <div className="flex flex-col">
                        <span className="border border-slate-900 bg-white px-1.5 py-0.5 industrial-label text-[10px] inline-block w-fit">{c.referred_by}</span>
                        {(c.referred_by_name || c.referred_by_phone) && (
                          <span className="mt-1 font-mono text-[11px] text-slate-700">
                            {c.referred_by_name || ""}{c.referred_by_name && c.referred_by_phone ? " · " : ""}{c.referred_by_phone || ""}
                          </span>
                        )}
                      </div>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3 font-mono">{c.avg_rating?.toFixed(1) || "—"}</td>
                  <td className="px-4 py-3"><StatusBadge s={c.status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <Link to={`/contractor/${c.id}`} className="border border-slate-900 bg-white p-1.5 hover:bg-slate-100" title="View" data-testid={`admin-view-${c.id}`}>
                        <Eye className="h-3.5 w-3.5" />
                      </Link>
                      {can("edit_contractors") && (
                        <button onClick={() => setEditing(c)} data-testid={`admin-edit-${c.id}`} className="border border-slate-900 bg-white p-1.5 hover:bg-slate-100" title="Edit">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {can("edit_contractors") && (
                        <button onClick={() => convertType(c)} data-testid={`admin-convert-${c.id}`} className="border border-slate-900 bg-white p-1.5 hover:bg-blueprint hover:text-white transition-colors" title={c.is_payroll ? "Convert to Contractor" : "Convert to Payroll Manpower"}>
                          <ArrowLeftRight className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {c.status !== "approved" && can("approve_contractors") && (
                        <button onClick={() => setApproving(c)} data-testid={`admin-approve-${c.id}`} className="border border-slate-900 bg-ncc-gold p-1.5 hover:-translate-y-0.5 transition-transform" title="Approve">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {c.status !== "rejected" && can("reject_contractors") && (
                        <button onClick={() => setStatus(c.id, "rejected")} data-testid={`admin-reject-${c.id}`} className="border border-slate-900 bg-white p-1.5 hover:bg-slate-100" title="Reject">
                          <Ban className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {c.contact_email && can("approve_contractors") && (
                        <button onClick={() => resendEmail(c)} data-testid={`admin-resend-${c.id}`} className="border border-slate-900 bg-white p-1.5 hover:bg-blueprint hover:text-white transition-colors" title="Re-send email">
                          <Mail className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {can("delete_contractors") && (
                        <button onClick={() => del(c.id)} data-testid={`admin-delete-${c.id}`} className="border border-slate-900 bg-white p-1.5 hover:bg-red-50" title="Delete">
                          <Trash2 className="h-3.5 w-3.5 text-red-600" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}

        {/* Mobile cards — Contractor / Payroll view */}
        {(entity === "contractor" || entity === "payroll") && (
        <div className="mt-6 space-y-3 md:hidden" data-testid="admin-mobile-cards">
          {loadingList ? (
            <div className="border-2 border-slate-300 bg-white p-6 text-center font-mono text-sm text-slate-500">Loading…</div>
          ) : visibleContractors.length === 0 ? (
            <div className="border-2 border-dashed border-slate-300 bg-white p-6 text-center font-mono text-sm text-slate-500">No {entity === "payroll" ? "payroll manpower" : "contractors"}</div>
          ) : visibleContractors.map((c) => (
            <div key={c.id} className="border-2 border-slate-900 bg-white p-4 shadow-brutal-sm" data-testid={`admin-mcard-${c.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    {c.is_payroll
                      ? <span className="inline-flex items-center border border-slate-900 bg-blueprint px-1.5 py-0.5 text-[10px] font-bold tracking-wider text-white">PAYROLL</span>
                      : <span className="inline-flex items-center border border-slate-900 bg-ncc-gold px-1.5 py-0.5 text-[10px] font-bold tracking-wider text-slate-900">CONTRACTOR</span>}
                    <div className="font-heading text-base font-bold text-slate-900">{c.name}</div>
                  </div>
                  <div className="mt-0.5 truncate font-mono text-xs text-slate-600">{c.location}</div>
                </div>
                <StatusBadge s={c.status} />
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {c.current_status === "NCCL"
                  ? <span className="border border-slate-900 bg-ncc-gold px-1.5 py-0.5 industrial-label">NCCL · {c.ncc_site_name || "—"}</span>
                  : <span className="border border-slate-900 bg-white px-1.5 py-0.5 industrial-label">NEW</span>}
                {(c.trades || []).slice(0, 3).map((t) => (
                  <span key={t} className="border border-slate-900 px-1.5 py-0.5 industrial-label">{t}</span>
                ))}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 border-t border-slate-200 pt-3 text-center text-xs">
                <div><div className="industrial-label text-slate-500">Crew</div><div className="font-mono font-bold">{c.crew_size}</div></div>
                <div><div className="industrial-label text-slate-500">Exp</div><div className="font-mono font-bold">{c.experience_years}y</div></div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Link to={`/contractor/${c.id}`} className="flex flex-1 min-w-[90px] items-center justify-center gap-1 border-2 border-slate-900 bg-white px-3 py-2 industrial-label" data-testid={`admin-mview-${c.id}`}>
                  <Eye className="h-4 w-4" /> View
                </Link>
                {can("edit_contractors") && (
                  <button onClick={() => setEditing(c)} data-testid={`admin-medit-${c.id}`} className="flex flex-1 min-w-[90px] items-center justify-center gap-1 border-2 border-slate-900 bg-white px-3 py-2 industrial-label">
                    <Pencil className="h-4 w-4" /> Edit
                  </button>
                )}
                {can("edit_contractors") && (
                  <button onClick={() => convertType(c)} data-testid={`admin-mconvert-${c.id}`} className="flex flex-1 min-w-[120px] items-center justify-center gap-1 border-2 border-slate-900 bg-white px-3 py-2 industrial-label">
                    <ArrowLeftRight className="h-4 w-4" />
                    {c.is_payroll ? "→ Contractor" : "→ Payroll"}
                  </button>
                )}
                {c.status !== "approved" && can("approve_contractors") && (
                  <button onClick={() => setApproving(c)} data-testid={`admin-mapprove-${c.id}`} className="flex flex-1 min-w-[90px] items-center justify-center gap-1 border-2 border-slate-900 bg-ncc-gold px-3 py-2 industrial-label">
                    <CheckCircle2 className="h-4 w-4" /> Approve
                  </button>
                )}
                {c.status !== "rejected" && can("reject_contractors") && (
                  <button onClick={() => setStatus(c.id, "rejected")} data-testid={`admin-mreject-${c.id}`} className="flex flex-1 min-w-[90px] items-center justify-center gap-1 border-2 border-slate-900 bg-white px-3 py-2 industrial-label">
                    <Ban className="h-4 w-4" /> Reject
                  </button>
                )}
                {c.contact_email && can("approve_contractors") && (
                  <button onClick={() => resendEmail(c)} data-testid={`admin-mresend-${c.id}`} className="flex flex-1 min-w-[90px] items-center justify-center gap-1 border-2 border-slate-900 bg-white px-3 py-2 industrial-label">
                    <Mail className="h-4 w-4" /> Re-send
                  </button>
                )}
                {can("delete_contractors") && (
                  <button onClick={() => del(c.id)} data-testid={`admin-mdelete-${c.id}`} className="flex items-center justify-center gap-1 border-2 border-slate-900 bg-white px-3 py-2 industrial-label text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        )}

        {/* Workmen Roster view */}
        {entity === "workmen" && (
          <div className="mt-6 overflow-x-auto border-2 border-slate-900 bg-white" data-testid="status-workmen-table">
            <table className="w-full text-sm">
              <thead className="bg-ncc-navy text-white">
                <tr>
                  {["Name", "Contractor", "Trade", "Mobile", "NCC Site", "Status", "Active", "Created", "Actions", "Next Actions"].map((h) => (
                    <th key={h} className="px-4 py-3 text-left industrial-label">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loadingList ? (
                  <tr><td colSpan={10} className="p-6 text-center font-mono text-slate-500">Loading…</td></tr>
                ) : visibleWorkmen.length === 0 ? (
                  <tr><td colSpan={10} className="p-6 text-center font-mono text-slate-500">No workmen found</td></tr>
                ) : visibleWorkmen.map((w) => (
                  <tr key={w.id} className="border-b border-slate-200 align-top hover:bg-slate-50" data-testid={`status-workman-row-${w.id}`}>
                    <td className="px-4 py-3 font-heading font-bold text-slate-900">{w.name}</td>
                    <td className="px-4 py-3 text-slate-700" data-testid={`status-workman-contractor-${w.id}`}>
                      {w.contractor_name
                        ? (w.contractor_id
                          ? <Link to={`/contractor/${w.contractor_id}`} className="hover:text-ncc-navy hover:underline">{w.contractor_name}</Link>
                          : w.contractor_name)
                        : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-3">{w.trade || "—"}</td>
                    <td className="px-4 py-3 font-mono">{w.mobile || "—"}</td>
                    <td className="px-4 py-3">{w.ncc_site_name || "—"}</td>
                    <td className="px-4 py-3">
                      {w.status === "approved" && <span className="inline-flex items-center gap-1 rounded-full border border-emerald-600 bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-700"><BadgeCheck className="h-3 w-3"/> APPROVED</span>}
                      {w.status === "rejected" && <span className="inline-flex items-center gap-1 rounded-full border border-red-600 bg-red-50 px-2 py-0.5 text-[11px] font-bold text-red-700"><Ban className="h-3 w-3"/> REJECTED</span>}
                      {(w.status === "pending" || !w.status) && <span className="inline-flex items-center gap-1 rounded-full border border-amber-600 bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-700"><Clock className="h-3 w-3"/> PENDING</span>}
                    </td>
                    <td className="px-4 py-3">
                      {w.is_active === false
                        ? <span className="inline-flex items-center gap-1 rounded-full border border-slate-500 bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-700"><PowerOff className="h-3 w-3"/> DE-ACTIVE{w.last_working_day ? ` · ${new Date(w.last_working_day).toLocaleDateString("en-GB")}` : ""}</span>
                        : <span className="inline-flex items-center gap-1 rounded-full border border-emerald-600 bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-700">ACTIVE</span>}
                    </td>
                    <td className="px-4 py-3 font-mono text-[11px] text-slate-600">{w.created_at ? new Date(w.created_at).toLocaleDateString("en-GB") : "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {(w.status === "pending" || !w.status) && can("approve_workmen") && (
                          <button
                            type="button"
                            onClick={() => approveWorkman(w)}
                            data-testid={`status-workman-approve-${w.id}`}
                            title="Approve workman"
                            className="rounded-md border border-emerald-300 bg-white p-1.5 text-emerald-700 hover:border-emerald-600 hover:bg-emerald-50"
                          ><Check className="h-3.5 w-3.5" /></button>
                        )}
                        {(w.status === "pending" || !w.status) && can("approve_workmen") && (
                          <button
                            type="button"
                            onClick={() => rejectWorkman(w)}
                            data-testid={`status-workman-reject-${w.id}`}
                            title="Reject workman (with reason)"
                            className="rounded-md border border-red-300 bg-white p-1.5 text-red-700 hover:border-red-600 hover:bg-red-50"
                          ><Ban className="h-3.5 w-3.5" /></button>
                        )}
                        {can("edit_workmen") && (
                          <button
                            type="button"
                            onClick={() => editWorkman(w)}
                            data-testid={`status-workman-edit-${w.id}`}
                            title="Edit workman"
                            className="rounded-md border border-ncc-navy/40 bg-white p-1.5 text-ncc-navy hover:border-ncc-navy hover:bg-ncc-tint"
                          ><Pencil className="h-3.5 w-3.5" /></button>
                        )}
                        {can("edit_workmen") && (
                          <button
                            type="button"
                            onClick={() => toggleWorkmanActive(w)}
                            data-testid={`status-workman-toggle-active-${w.id}`}
                            title={w.is_active === false ? "Re-activate workman" : "De-activate workman (set LWD)"}
                            className={`rounded-md border p-1.5 ${
                              w.is_active === false
                                ? "border-emerald-300 bg-white text-emerald-700 hover:border-emerald-600 hover:bg-emerald-50"
                                : "border-slate-300 bg-white text-slate-700 hover:border-slate-500 hover:bg-slate-50"
                            }`}
                          >
                            {w.is_active === false ? <Power className="h-3.5 w-3.5" /> : <PowerOff className="h-3.5 w-3.5" />}
                          </button>
                        )}
                        {w.contractor_id && (
                          <Link
                            to={`/contractor/${w.contractor_id}`}
                            data-testid={`status-workman-view-${w.id}`}
                            title="Open parent contractor"
                            className="inline-flex rounded-md border border-slate-300 bg-white p-1.5 text-slate-700 hover:border-ncc-navy hover:text-ncc-navy"
                          ><Eye className="h-3.5 w-3.5" /></Link>
                        )}
                        {can("delete_contractors") && (
                          <button
                            type="button"
                            onClick={() => deleteWorkman(w)}
                            data-testid={`status-workman-delete-${w.id}`}
                            title="Delete workman"
                            className="rounded-md border border-slate-300 bg-white p-1.5 text-red-600 hover:border-red-600 hover:bg-red-50"
                          ><Trash2 className="h-3.5 w-3.5" /></button>
                        )}
                      </div>
                    </td>
                    {/* Next Actions · Printing — active ONLY when status === approved */}
                    <td className="px-4 py-3">
                      {w.status === "approved" ? (
                        <div className="flex items-center gap-1.5" data-testid={`status-workman-printing-${w.id}`}>
                          <button
                            type="button"
                            onClick={() => printFormXI(w, null)}
                            data-testid={`status-workman-print-formxi-${w.id}`}
                            title="Print Form-XI · Medical Certificate (BOCW Rule 223(c))"
                            className="rounded-md border border-emerald-300 bg-white p-1.5 text-emerald-700 hover:border-emerald-600 hover:bg-emerald-50"
                          ><Stethoscope className="h-3.5 w-3.5" /></button>
                          <button
                            type="button"
                            onClick={() => printScreeningOrIdCard(w, "screening")}
                            data-testid={`status-workman-print-screening-${w.id}`}
                            title="Print Worker's Screening Form"
                            className="rounded-md border border-slate-300 bg-white p-1.5 text-slate-700 hover:border-ncc-navy hover:text-ncc-navy"
                          ><ClipboardList className="h-3.5 w-3.5" /></button>
                          <button
                            type="button"
                            onClick={() => printScreeningOrIdCard(w, "idcard")}
                            data-testid={`status-workman-print-idcard-${w.id}`}
                            title="Print Identity Card (Front & Back)"
                            className="rounded-md border border-amber-300 bg-white p-1.5 text-amber-700 hover:border-amber-600 hover:bg-amber-50"
                          ><CreditCard className="h-3.5 w-3.5" /></button>
                        </div>
                      ) : (
                        <span className="font-mono text-[11px] text-slate-400" title="Printing unlocks after approval">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Payroll Roster (Medical / Induction) view */}
        {entity === "payroll-roster" && (
          <div className="mt-6 overflow-x-auto border-2 border-slate-900 bg-white" data-testid="status-pay-roster-table">
            <table className="w-full text-sm">
              <thead className="bg-ncc-navy text-white">
                <tr>
                  {["Name", "Trade", "Mobile", "NCC Site", "Source", "Pay-Roster Status", "Submitted", "Actions", "Next Actions"].map((h) => (
                    <th key={h} className="px-4 py-3 text-left industrial-label">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loadingList ? (
                  <tr><td colSpan={9} className="p-6 text-center font-mono text-slate-500">Loading…</td></tr>
                ) : visiblePayRoster.length === 0 ? (
                  <tr><td colSpan={9} className="p-6 text-center font-mono text-slate-500">No Payroll Roster records found</td></tr>
                ) : visiblePayRoster.map((c) => {
                  const prs = c.pay_roster_status || "not_submitted";
                  return (
                    <tr key={c.id} className="border-b border-slate-200 align-top hover:bg-slate-50" data-testid={`status-pay-roster-row-${c.id}`}>
                      <td className="px-4 py-3 font-heading font-bold text-slate-900">{c.name}</td>
                      <td className="px-4 py-3">{(c.trades && c.trades[0]) || "—"}</td>
                      <td className="px-4 py-3 font-mono">{c.contact_phone || "—"}</td>
                      <td className="px-4 py-3">{c.ncc_site_name || "—"}</td>
                      <td className="px-4 py-3">{c.source ? `${c.source}${c.source_agency_name ? ` (${c.source_agency_name})` : ""}` : "—"}</td>
                      <td className="px-4 py-3">
                        {prs === "approved" && <span className="inline-flex items-center gap-1 rounded-full border border-emerald-600 bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-700"><BadgeCheck className="h-3 w-3"/> APPROVED</span>}
                        {prs === "rejected" && <span className="inline-flex items-center gap-1 rounded-full border border-red-600 bg-red-50 px-2 py-0.5 text-[11px] font-bold text-red-700"><Ban className="h-3 w-3"/> REJECTED</span>}
                        {prs === "pending" && <span className="inline-flex items-center gap-1 rounded-full border border-amber-600 bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-700"><Clock className="h-3 w-3"/> PENDING</span>}
                        {prs === "not_submitted" && <span className="inline-flex items-center gap-1 rounded-full border border-slate-500 bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-700">NOT SUBMITTED</span>}
                      </td>
                      <td className="px-4 py-3 font-mono text-[11px] text-slate-600">{c.pay_roster_submitted_at ? new Date(c.pay_roster_submitted_at).toLocaleDateString("en-GB") : "—"}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          {prs === "pending" && can("approve_payroll_roster") && (
                            <button
                              type="button"
                              onClick={() => approvePayRoster(c)}
                              data-testid={`status-pay-roster-approve-${c.id}`}
                              title="Approve Pay Roster (Medical / Induction)"
                              className="rounded-md border border-emerald-300 bg-white p-1.5 text-emerald-700 hover:border-emerald-600 hover:bg-emerald-50"
                            ><Check className="h-3.5 w-3.5" /></button>
                          )}
                          {prs === "pending" && can("approve_payroll_roster") && (
                            <button
                              type="button"
                              onClick={() => rejectPayRoster(c)}
                              data-testid={`status-pay-roster-reject-${c.id}`}
                              title="Reject Pay Roster (with reason)"
                              className="rounded-md border border-red-300 bg-white p-1.5 text-red-700 hover:border-red-600 hover:bg-red-50"
                            ><Ban className="h-3.5 w-3.5" /></button>
                          )}
                          {(prs === "pending" || prs === "rejected") && (can("edit_contractors") || can("approve_medical") || can("approve_payroll_roster")) && (
                            <button
                              type="button"
                              onClick={() => setEditingPayRoster(c)}
                              data-testid={`status-pay-roster-edit-${c.id}`}
                              title={prs === "rejected" ? "Edit & resubmit — saving re-queues this record as PENDING" : "Edit this submission"}
                              className="rounded-md border border-slate-300 bg-white p-1.5 text-slate-700 hover:border-ncc-navy hover:text-ncc-navy"
                            ><Pencil className="h-3.5 w-3.5" /></button>
                          )}
                          {prs === "rejected" && can("delete_contractors") && (
                            <button
                              type="button"
                              onClick={async () => {
                                if (!window.confirm(`Delete rejected record "${c.name}" permanently? This cannot be undone.`)) return;
                                try {
                                  await api.delete(`/contractors/${c.id}`);
                                  toast.success(`Deleted · ${c.name}`);
                                  await Promise.all([loadStats(), loadList()]);
                                } catch (err) {
                                  toast.error(err?.response?.data?.detail || "Delete failed");
                                }
                              }}
                              data-testid={`status-pay-roster-delete-${c.id}`}
                              title="Delete this rejected record permanently"
                              className="rounded-md border border-red-300 bg-white p-1.5 text-red-700 hover:border-red-600 hover:bg-red-50"
                            ><Trash2 className="h-3.5 w-3.5" /></button>
                          )}
                          {c.id && (
                            <Link
                              to={`/contractor/${c.id}`}
                              data-testid={`status-pay-roster-view-${c.id}`}
                              title="Open contractor profile"
                              className="inline-flex rounded-md border border-slate-300 bg-white p-1.5 text-slate-700 hover:border-ncc-navy hover:text-ncc-navy"
                            ><Eye className="h-3.5 w-3.5" /></Link>
                          )}
                        </div>
                      </td>
                      {/* Next Actions · Printing — Form-XI / Screening / ID Card after pay_roster approval */}
                      <td className="px-4 py-3">
                        {prs === "approved" ? (
                          <div className="flex items-center gap-1.5" data-testid={`status-pay-roster-printing-${c.id}`}>
                            <button
                              type="button"
                              onClick={() => printFormXI(_payrollToWorkman(c), null)}
                              data-testid={`status-pay-roster-print-formxi-${c.id}`}
                              title="Print Form-XI · Medical Certificate (BOCW Rule 223(c))"
                              className="rounded-md border border-emerald-300 bg-white p-1.5 text-emerald-700 hover:border-emerald-600 hover:bg-emerald-50"
                            ><Stethoscope className="h-3.5 w-3.5" /></button>
                            <button
                              type="button"
                              onClick={() => printScreeningForm(_payrollToWorkman(c), null, [])}
                              data-testid={`status-pay-roster-print-screening-${c.id}`}
                              title="Print Worker's Screening Form"
                              className="rounded-md border border-slate-300 bg-white p-1.5 text-slate-700 hover:border-ncc-navy hover:text-ncc-navy"
                            ><ClipboardList className="h-3.5 w-3.5" /></button>
                            <button
                              type="button"
                              onClick={() => printIdentityCard(_payrollToWorkman(c), null, [])}
                              data-testid={`status-pay-roster-print-idcard-${c.id}`}
                              title="Print Identity Card (Front & Back)"
                              className="rounded-md border border-amber-300 bg-white p-1.5 text-amber-700 hover:border-amber-600 hover:bg-amber-50"
                            ><CreditCard className="h-3.5 w-3.5" /></button>
                          </div>
                        ) : (
                          <span className="font-mono text-[11px] text-slate-400" title="Printing unlocks after Pay Roster approval">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <EditContractorModal
          contractor={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await Promise.all([loadStats(), loadList()]); }}
        />
      )}

      {approving && (
        <ApproveModal
          contractor={approving}
          onClose={() => setApproving(null)}
          onConfirm={(asPayroll) => approveWithType(approving, asPayroll)}
        />
      )}

      <PayrollScreenFormDrawer
        open={!!editingPayRoster}
        contractor={editingPayRoster}
        authToken={typeof window !== "undefined" ? window.localStorage.getItem("ccdb_token") : ""}
        adminMode
        onClose={() => setEditingPayRoster(null)}
        onSaved={async () => {
          setEditingPayRoster(null);
          await Promise.all([loadStats(), loadList()]);
        }}
      />

      {workmenDrawerOpen && editingWorkman && (
        <WorkmenDrawer
          open={workmenDrawerOpen}
          onClose={() => { setWorkmenDrawerOpen(false); setEditingWorkman(null); }}
          mode="admin"
          contractorId={editingWorkman.contractor?.id}
          contractorName={editingWorkman.contractor?.name}
          crewSize={editingWorkman.contractor?.crew_size}
          authToken={localStorage.getItem("ccdb_token")}
          editingWorkmanId={editingWorkman.workmanId}
          onChanged={loadList}
        />
      )}
    </div>
  );
}

function ApproveModal({ contractor, onClose, onConfirm }) {
  const [asPayroll, setAsPayroll] = useState(!!contractor.is_payroll);
  const [submitting, setSubmitting] = useState(false);
  const willChange = !!contractor.is_payroll !== asPayroll;

  async function handleConfirm() {
    setSubmitting(true);
    try { await onConfirm(asPayroll); } finally { setSubmitting(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4" data-testid="approve-modal">
      <div className="w-full max-w-md border-2 border-slate-900 bg-white shadow-brutal">
        <div className="flex items-center justify-between border-b-2 border-slate-900 bg-ncc-navy px-5 py-3 text-white">
          <div className="industrial-label">// Approve Registration</div>
          <button onClick={onClose} data-testid="approve-modal-close" className="hover:text-ncc-gold"><X className="h-4 w-4" /></button>
        </div>
        <div className="px-5 py-5">
          <div className="font-heading text-lg font-bold text-slate-900">{contractor.name}</div>
          <div className="mt-1 font-mono text-xs text-slate-600">{contractor.contact_phone}{contractor.contact_email ? ` · ${contractor.contact_email}` : ""}</div>

          <div className="mt-5 industrial-label text-slate-500">Approve this record as</div>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setAsPayroll(false)} data-testid="approve-type-contractor"
              className={`border-2 px-3 py-3 text-left transition-all ${!asPayroll ? "border-slate-900 bg-ncc-gold shadow-brutal-sm" : "border-slate-300 bg-white hover:border-slate-900"}`}>
              <div className="font-heading text-sm font-bold uppercase tracking-wider text-slate-900">Contractor</div>
              <div className="mt-0.5 font-mono text-[11px] text-slate-600">External crew leader</div>
            </button>
            <button type="button" onClick={() => setAsPayroll(true)} data-testid="approve-type-payroll"
              className={`border-2 px-3 py-3 text-left transition-all ${asPayroll ? "border-slate-900 bg-blueprint text-white shadow-brutal-sm" : "border-slate-300 bg-white hover:border-slate-900"}`}>
              <div className="font-heading text-sm font-bold uppercase tracking-wider">Payroll Manpower</div>
              <div className={`mt-0.5 font-mono text-[11px] ${asPayroll ? "text-blue-100" : "text-slate-600"}`}>NCC payroll employee</div>
            </button>
          </div>

          {willChange && (
            <div className="mt-4 border-l-4 border-ncc-gold bg-amber-50 px-3 py-2 font-mono text-xs text-amber-900" data-testid="approve-type-warning">
              Heads up: registration will be re-classified from <b>{contractor.is_payroll ? "Payroll Manpower" : "Contractor"}</b> to <b>{asPayroll ? "Payroll Manpower" : "Contractor"}</b>.
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t-2 border-slate-900 bg-slate-50 px-5 py-3">
          <button onClick={onClose} disabled={submitting} data-testid="approve-modal-cancel"
            className="border-2 border-slate-900 bg-white px-4 py-2 industrial-label hover:bg-slate-100 disabled:opacity-50">Cancel</button>
          <button onClick={handleConfirm} disabled={submitting} data-testid="approve-modal-confirm"
            className="inline-flex items-center gap-2 border-2 border-slate-900 bg-ncc-gold px-4 py-2 industrial-label transition-all hover:-translate-y-0.5 hover:shadow-brutal-sm disabled:opacity-50">
            <CheckCircle2 className="h-4 w-4" /> {submitting ? "Approving…" : "Confirm Approve"}
          </button>
        </div>
      </div>
    </div>
  );
}
